<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="w-full mb-4 flex flex-col sm:flex-row gap-2">
                <app-drop-down-button :placeholder="$t('deductions.new')" @input="addDeduction">
                    <option value="caution">{{ $t('deductions.types.caution') }}</option>
                    <option value="garanteeDeduction">{{ $t('deductions.types.garanteeDeduction') }}</option>
                    <option value="penalty">{{ $t('deductions.types.penalty') }}</option>
                    <option value="deduction">{{ $t('deductions.types.deduction') }}</option>
                </app-drop-down-button>
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
            <div class="flex justify-between">
                <div>
                    <app-select @input="onAction" v-if="!loading && selection.length > 0" class="text-xs">
                        <option value="" disabled selected>{{ $t('commons.actions') }}</option>
                        <option value="modify">{{ $t('commons.actionModify') }}</option>
                        <option value="delete">{{ $t('commons.actionDelete') }}</option>
                    </app-select>
                </div>
            </div>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 2rem" class="hidden sm:table-cell text-left border-r p-1">
                            <app-checkbox
                                :value="selection.length === filteredItems.length && selection.length > 0"
                                :indeterminate="selection.length > 0 && selection.length < filteredItems.length"
                                :show-label="false"
                                :label="$t('commons.toggleSelectAll')"
                                @input="toggleSelectAll"
                            ></app-checkbox>
                        </th>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emitter')"
                            >
                                {{ $t('commons.bundle') }}
                                <div v-if="sortKey === 'emitter'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('object')"
                            >
                                {{ $t('deductions.object') }}
                                <div v-if="sortKey === 'object'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div>
                                {{ $t('commons.type') }}
                            </div>
                        </th>
                        <th class="text-center border-r p-1" style="width: 4rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emissionDate')"
                            >
                                {{ $t('deductions.emissionDate') }}
                                <div v-if="sortKey === 'emissionDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <div class="flex flex-col">
                                <span>{{ $t('commons.amountTTC') }}</span>
                                ({{ $n(filteredTotal, currencyFormat) }})
                            </div>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <span>{{ $t('commons.status') }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="deduction in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="hidden sm:table-cell border-r p-1">
                                <a :id="'uuid_' + deduction.id" style="scroll-margin-top: 3em"></a>
                                <app-checkbox
                                    :value="deduction.isSelected"
                                    :label="$t('commons.select')"
                                    :show-label="false"
                                    @input="saveSelection(deduction)"
                                ></app-checkbox>
                            </td>
                            <td class="text-left border-r p-1">
                                <div
                                    :title="
                                        deduction.bundle && deduction.bundle.company
                                            ? deduction.bundle.company.name
                                            : ''
                                    "
                                >
                                    {{ deduction.bundle ? deduction.bundle.label : '' }}
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'followDeduction',
                                        params: {
                                            ...$route.params,
                                            deductionId: deduction.id,
                                        },
                                    }"
                                >
                                    <span class="font-bold">{{ deduction.object }}</span>
                                    <span v-if="deduction.code" class="mx-1">#{{ deduction.code }}</span>
                                    <span v-if="!deduction.object || deduction.object.trim().length === 0">...</span>
                                </router-link>
                            </td>
                            <td class="text-left border-r p-1">
                                {{ deduction.type ? $t('deductions.types.' + deduction.type) : '' }}
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                <app-date-link
                                    class="w-full"
                                    @enter="focusToNextDate(deduction)"
                                    :ref="'date_' + deduction.id"
                                    :label="$t('deductions.emissionDate')"
                                    :show-label="false"
                                    v-model="deduction.emissionDate"
                                    @input="updateDate(deduction, $event)"
                                ></app-date-link>
                            </td>
                            <td class="border-r p-1">
                                <app-number-link
                                    v-model="deduction.amountTTC"
                                    format="double"
                                    :positiveOnly="false"
                                    @input="saveDeduction(deduction, $event)"
                                >
                                    <u class="text-right w-full">
                                        {{ deduction.amountTTC ? $n(deduction.amountTTC, currencyFormat) : '-' }}
                                    </u>
                                </app-number-link>
                            </td>
                            <td class="text-center border-r p-1">
                                {{
                                    deduction.deductionStatus
                                        ? $t('deductions.status.' + deduction.deductionStatus)
                                        : '-'
                                }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
        <app-popup ref="modifyAllPopup" :showHeader="true" :title="$t('commons.actionOnSelection')">
            <ValidationObserver v-slot="{ invalid }" tag="form" ref="observer" class="p-2 gap-2 flex flex-col">
                <app-bundle-picker
                    v-model="editedDeduction.bundle"
                    :options="bundles"
                    :label="$t('deductions.bundle')"
                    :companyLabel="$t('commons.company')"
                ></app-bundle-picker>
                <app-date-input v-model="editedDeduction.emissionDate" :label="$t('deductions.emissionDate')" />
                <app-select :label="$t('commons.status')" v-model="editedDeduction.deductionStatus">
                    <option value=""></option>
                    <option value="provisional">{{ $t('deductions.status.provisional') }}</option>
                    <option value="definitive">{{ $t('deductions.status.definitive') }}</option>
                    <option value="obsolete">{{ $t('deductions.status.obsolete') }}</option>
                </app-select>
                <app-footer @click="saveMultiple" :disabled="invalid" class="mt-2"></app-footer>
            </ValidationObserver>
        </app-popup>
    </main>
</template>

<script>
import { isMobile } from '@/state/state';
import { filterMatch, get, getMapById, sortBy } from '@/services/sanitize.service';
import { confirm } from '@/features/dialogs/dialogs.service';
import { queryProject } from '@/features/projects/projects.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import { humanizeDate } from '@/filters/dateFilter';
import {
    createDeduction,
    getDeductions,
    removeDeduction,
    updateDeduction,
} from '@/features/deductions/deductions.service';
import AppDropDownButton from '@/components/appDropDownButton/AppDropDownButton.vue';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import IconRotateRight from '@/icons/IconRotateRight.vue';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox.vue';
import AppDateLink from '@/components/appDateLink/AppDateLink.vue';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions.vue';
import AppPopup from '@/components/app-popup/AppPopup.vue';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppFooter from '@/components/appFooter/AppFooter.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';

export default {
    components: {
        AppNumberInput,
        AppNumberLink,
        AppFooter,
        AppDateInput,
        AppBundlePicker,
        AppPopup,
        AppQuickActions,
        AppDateLink,
        AppCheckbox,
        IconRotateRight,
        AppSelect,
        AppMultiPicker,
        AppDropDownButton,
    },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filterValue).map((item) => ({
                ...item,
                isSelected: this.selection.includes(item.id),
            }));
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    const text = item.code ? item.code + ' ' + item.object : item.object;
                    if (this.sortKey === 'object') {
                        return text;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate + ' ' + text;
                    } else if (this.sortKey === 'emitter') {
                        return (item.bundle ? item.bundle.label : null) + text;
                    } else if (this.sortKey === 'deductionStatus') {
                        return this.$t('deductions.status.' + item.deductionStatus);
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filteredTotal() {
            return this.filteredItems.reduce((acc, item) => acc + item.amountTTC, 0);
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: this.$t('commons.status'),
                    children: [
                        {
                            id: 'provisional',
                            name: this.$t('deductions.status.provisional'),
                            firstOfCriteriaType: true,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'definitive',
                            name: this.$t('deductions.status.definitive'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'obsolete',
                            name: this.$t('deductions.status.obsolete'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.type'),
                    id: this.$t('commons.type'),
                    children: [
                        {
                            id: 'caution',
                            name: this.$t('deductions.types.caution'),
                            firstOfCriteriaType: true,
                            _isTypeCriteria: true,
                        },
                        {
                            id: 'deduction',
                            name: this.$t('deductions.types.deduction'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                        {
                            id: 'garanteeDeduction',
                            name: this.$t('deductions.types.garanteeDeduction'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                        {
                            id: 'penalty',
                            name: this.$t('deductions.types.penalty'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        humanizeDate,
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getDeductions(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                ]).subscribe(async ([bundles, deductions, companies]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.restoreSelection();
                    this.items = deductions.map((deduction) => {
                        const bundle = this.bundleMap[deduction.bundleId];
                        return {
                            ...deduction,
                            bundle,
                            filterString: [
                                deduction.code,
                                deduction.object,
                                deduction.bundleId && bundle ? bundle.label : '',
                            ].join(),
                            isSelected: this.selection.includes(deduction.id),
                        };
                    });
                    this.cleanupSavedSelection();
                    this.scrollToLastVisited();
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedDeductionId = localStorage.getItem(
                'deduction.lastVisitedDeductionId.' + this.$route.params.projectId,
            );
            if (lastVisitedDeductionId) {
                setTimeout(() => {
                    const element = this.$el.querySelector('#uuid_' + lastVisitedDeductionId);
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        saveMultiple() {
            this.selection.map((itemId) => {
                const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
                const patch = {};
                if (this.editedDeduction.emissionDate) {
                    patch.emissionDate = this.editedDeduction.emissionDate;
                } else if (this.getSelectionCommonEmissionDate(selectedItems)) {
                    patch.emissionDate = null;
                }
                if (this.editedDeduction.bundle) {
                    patch.bundleId = this.editedDeduction.bundle.id;
                } else if (this.getSelectionCommonBundle(selectedItems)) {
                    patch.bundleId = null;
                }
                if (this.editedDeduction.deductionStatus) {
                    patch.deductionStatus = this.editedDeduction.deductionStatus;
                } else if (this.getSelectionCommonStatus(selectedItems)) {
                    patch.deductionStatus = null;
                }
                return updateDeduction(this.$route.params.projectId, { id: itemId, ...patch });
            });
            this.$refs.modifyAllPopup.close();
        },
        async onAction(action) {
            const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
            if (action === 'delete') {
                if (await confirm(this.$t('commons.confirmMessageAll', { number: this.selection.length }))) {
                    await Promise.all(
                        selectedItems.map((item) => removeDeduction(this.$route.params.projectId, item.id)),
                    );
                    this.selection = [];
                }
            } else if (action === 'modify') {
                this.editedDeduction = {
                    emissionDate: this.getSelectionCommonEmissionDate(selectedItems),
                    bundle: this.getSelectionCommonBundle(selectedItems),
                    deductionStatus: this.getSelectionCommonStatus(selectedItems),
                };
                this.$refs.modifyAllPopup.open();
            }
        },
        getSelectionCommonBundle(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.bundle);
            if (firstItem && selectedItems.every((item) => item.bundle && item.bundle.id === firstItem.bundle.id)) {
                return firstItem.bundle;
            } else {
                return null;
            }
        },
        getSelectionCommonEmissionDate(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emissionDate);
            if (
                firstItem &&
                selectedItems.every(
                    (item) => item.emissionDate && item.emissionDate.getTime() === firstItem.emissionDate.getTime(),
                )
            ) {
                return firstItem.emissionDate;
            } else {
                return null;
            }
        },
        getSelectionCommonStatus(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.deductionStatus);
            if (firstItem && selectedItems.every((item) => item.deductionStatus === firstItem.deductionStatus)) {
                return firstItem.deductionStatus;
            } else {
                return null;
            }
        },
        toggleSelectAll() {
            if (this.selection.length < this.filteredItems.length) {
                this.selection = this.filteredItems.map((item) => item.id);
            } else {
                this.selection = [];
            }
            this.saveSelection();
        },
        cleanupSavedSelection() {
            this.selection = this.selection.filter((itemId) => !!this.items.find((anItem) => anItem.id === itemId));
        },
        saveSelection(item) {
            if (item) {
                if (!item.isSelected) {
                    this.selection.push(item.id);
                } else {
                    this.selection = this.selection.filter((anItem) => anItem !== item.id);
                }
            }
            localStorage.setItem(
                'deductions.selection.' + this.$route.params.projectId,
                JSON.stringify(this.selection),
            );
        },
        restoreSelection() {
            const cache = localStorage.getItem('deductions.selection.' + this.$route.params.projectId);
            if (cache) {
                this.selection = JSON.parse(cache);
            }
        },
        async addDeduction(type) {
            const result = await createDeduction(this.$route.params.projectId, {
                object: '',
                type,
            });
            await this.$router.push({
                name: 'followDeduction',
                params: {
                    projectId: this.$route.params.projectId,
                    deductionId: result.id,
                },
            });
        },
        matchBundleFilter(item, bundleIds) {
            return item.bundle && bundleIds.includes(item.bundle.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find((criteria) => criteria.id === item.deductionStatus);
        },
        matchType(criteria, item) {
            if (!criteria || criteria.length === 0) {
                return true;
            }
            return criteria.find((criteria) => item.type === criteria.id);
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'deductions_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('deductions_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.sortKey = parsedCache.sortKey || 'object';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            const typeCriteria = filter.filter((aCriteria) => aCriteria._isTypeCriteria);

            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                    typeCriteria: this.matchType(typeCriteria, item),
                };
                const filterResult = Object.values(fullCriteria).every((value) => !!value);
                if (!filterResult) {
                    this.selection = this.selection.filter((id) => id !== item.id);
                }
                return filterResult;
            });
        },
        updateDate(deduction, date) {
            updateDeduction(this.$route.params.projectId, {
                emissionDate: date,
                id: deduction.id,
            });
        },
        saveDeduction(deduction, amountTTC) {
            updateDeduction(this.$route.params.projectId, {
                id: deduction.id,
                amountTTC,
            });
        },
        focusToNextDate(deduction) {
            let index = this.filteredItems.findIndex((aDeduction) => aDeduction.id === deduction.id);
            let nextDeduction = this.filteredItems[index + 1];
            while (!nextDeduction && index < this.filteredItems.length) {
                nextDeduction = this.filteredItems[++index];
            }
            if (nextDeduction) {
                this.$emit('select', nextDeduction);
                const element = this.$refs['date_' + nextDeduction.id];
                if (element) {
                    if (Array.isArray(element)) {
                        element[0].focus();
                    } else {
                        element.focus();
                    }
                }
            }
        },
    },
    data() {
        return {
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            quickActions: [
                {
                    name: this.$t('deductions.newCaution'),
                    run: () => this.addDeduction('caution'),
                },
                {
                    name: this.$t('deductions.newDeduction'),
                    run: () => this.addDeduction('deduction'),
                },
                {
                    name: this.$t('deductions.newGaranteeDeduction'),
                    run: () => this.addDeduction('garanteeDeduction'),
                },
                {
                    name: this.$t('deductions.newPenalty'),
                    run: () => this.addDeduction('penalty'),
                },
            ],
            sortKey: 'name',
            sortAsc: true,
            selection: [],
            editedDeduction: {
                emissionDate: null,
                deductionStatus: null,
                bundle: null,
            },
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
        };
    },
};
</script>
